import { useCallback, useContext } from 'react';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { useNavigateToEntity } from '@/hooks/navigation';
import { SyncSystemContext } from '@/lib/sync/sync-system-context';
import { createSurface } from '@/lib/sync/create-surface';
import { useUIStore } from '@/stores/use-ui-store';

/**
 * Creates a new document, canvas, or collection in the active workspace.
 * Extracted from app.tsx and Sidebar so the application shell stays
 * composition-only — the feature owns its own create-action logic.
 */
export function useCreateEntity() {
  const workspaceService = useWorkspaceService();
  const navigateToEntity = useNavigateToEntity();
  // Read the context rather than useSyncSystem(): that throws without a
  // provider, and this hook's other actions have no need of one. A caller
  // outside the sync tree still gets a document, just without the surface.
  const registry = useContext(SyncSystemContext)?.registry ?? null;
  const requestSurface = useUIStore((s) => s.requestSurface);

  const createNewDocument = useCallback(async () => {
    const { id } = await workspaceService.createDoc({ kind: 'document' });
    navigateToEntity(id);
  }, [workspaceService, navigateToEntity]);

  const createNewCanvas = useCallback(async () => {
    const { id } = await workspaceService.createDoc({ kind: 'canvas' });
    navigateToEntity(id);
  }, [workspaceService, navigateToEntity]);

  /**
   * A document that opens on a fresh audio surface, ready to record into.
   *
   * The surface is minted here rather than left to the server's auto-create:
   * this caller knows the id before the write, so it can ask for that surface
   * to be the active one instead of waiting for the manifest to sync back and
   * guessing which entry is the new one.
   */
  const createNewRecording = useCallback(async () => {
    const { id } = await workspaceService.createDoc({ kind: 'document' });
    // Resolves once the doc is bound to local persistence — writing before
    // that lands the surface in a doc that is about to be replaced.
    const doc = registry ? await registry.subscribe(id, 'document') : null;
    // The generator marks this as a recording surface rather than a bare
    // audio one — the same shape the surface panel's Record action and the
    // server's auto-create both write, and what mounts the capture controls.
    const surfaceId = createSurface(doc, {
      title: 'Recording',
      surface: 'audio',
      generator: 'recording',
    });
    // A refused surface (at the cap, or a doc that never bound) still leaves
    // the user a document that is theirs; opening it beats stranding them.
    if (surfaceId) requestSurface(id, surfaceId);
    navigateToEntity(id);
  }, [workspaceService, registry, requestSurface, navigateToEntity]);

  const createNewCollection = useCallback(() => {
    workspaceService.createCollection('Untitled Collection');
  }, [workspaceService]);

  return { createNewDocument, createNewCanvas, createNewRecording, createNewCollection };
}
